/**
 * Общее состояние выпадающего поиска в ядре бандла (SAIT-80, план — раздел
 * «Выпадающий поиск и компонент Vue»).
 *
 * Сам компонент панели живёт в отдельной группе-расширении vpl.tas.search и в
 * общую склейку не входит: в браузер он едет только по требованию через
 * BX.Runtime.loadExtension — при наведении на поле-плейсхолдер (preload) или
 * при открытии. Здесь — только тонкий слой: реактивный флаг «открыто», фраза,
 * набранная в плейсхолдере до загрузки группы, и асинхронная обёртка
 * компонента для SearchPanelHost (один экземпляр панели на страницу).
 *
 * Медленная сеть (решение заказчика 2026-09-19): панель НЕ открывается
 * недостроенной. Пока группа едет, на экране ничего не появляется — ожидание
 * показывает только курсор ожидания на поле-плейсхолдере (searchState.pending);
 * полоса индикатора SPA-переходов здесь НЕ включается (правка заказчика
 * 2026-09-20: загрузка панели — не переход на другую страницу; до этого
 * ожидание держало полосу через holdCatalogPreloader). Поле остаётся рабочим:
 * курсор в нём, набранное не теряется и переезжает в панель в момент открытия. Прежде на это время подставлялась
 * заглушка из пустых белых блоков, и она вдобавок стояла на 76 px от верха
 * против 38 px у настоящей панели — при подмене блок заметно прыгал.
 * Ожидание снимается Escape (cancelPendingOpen).
 */
import { reactive, defineAsyncComponent } from 'ui.vue3';

export const SEARCH_EXTENSION = 'vpl.tas.search';
export const SEARCH_PANEL_COMPONENT = 'SearchPanel';

export const searchState = reactive({
    open: false,
    /** фраза из поля-плейсхолдера на момент открытия (ввод до загрузки группы не теряется) */
    initialQuery: '',
    /** какой плейсхолдер открыл панель: 'header' | 'fixed' | 'mobile' — для отладки и аналитики */
    source: null,
    /** группа едет, панель откроется готовой (для плейсхолдера: курсор ожидания) */
    pending: false,
});

let preloadPromise = null;

/** Загрузка группы заранее (наведение на поле); повторные вызовы — без запросов */
export function preloadSearchPanel() {
    if (!preloadPromise) {
        if (typeof BX === 'undefined' || !BX.Runtime || typeof BX.Runtime.loadExtension !== 'function') {
            return Promise.reject(new Error('vpl.tas: BX.Runtime.loadExtension недоступен'));
        }
        preloadPromise = BX.Runtime.loadExtension(SEARCH_EXTENSION).catch((e) => {
            preloadPromise = null; // следующая попытка — заново (сеть могла моргнуть)
            throw e;
        });
    }
    return preloadPromise;
}

function loadedPanel() {
    try {
        const group = BX.getClass('BX.Vpl.Search');
        return group && group[SEARCH_PANEL_COMPONENT] ? group[SEARCH_PANEL_COMPONENT] : null;
    } catch (e) {
        return null;
    }
}

/** ожидание загрузки группы: одно на страницу, отменяется Escape */
let waiting = null;

function releaseWaiting() {
    if (!waiting) {
        return;
    }
    document.removeEventListener('keydown', waiting.onKeydown, true);
    waiting = null;
    searchState.pending = false;
}

/** Escape во время ожидания — открывать уже не нужно (группа всё равно докачается) */
export function cancelPendingOpen() {
    releaseWaiting();
}

function showPanel(query, source, options) {
    if (options && typeof options.onOpen === 'function') {
        options.onOpen();
    }
    searchState.initialQuery = query;
    searchState.source = source;
    searchState.open = true;
}

/**
 * options.getQuery — что набрано в плейсхолдере на момент открытия (за время
 * ожидания посетитель мог дописать); options.onOpen — снять фокус с
 * плейсхолдера, когда панель действительно открывается.
 */
export function openSearch(initialQuery = '', source = null, options = {}) {
    // группа уже в браузере (обычный случай: preload по наведению) — открываем сразу
    if (loadedPanel()) {
        releaseWaiting();
        showPanel(initialQuery, source, options);
        return;
    }
    if (waiting) {
        return; // уже ждём: повторные клики по полю ничего не меняют
    }
    const current = {
        onKeydown: (e) => {
            if (e.key === 'Escape') {
                cancelPendingOpen();
            }
        },
    };
    waiting = current;
    searchState.pending = true;
    document.addEventListener('keydown', current.onKeydown, true);

    const open = () => {
        if (waiting !== current) {
            return; // ожидание отменили — панель не навязываем
        }
        const query = options && typeof options.getQuery === 'function' ? options.getQuery() : initialQuery;
        releaseWaiting();
        showPanel(query, source, options);
    };
    // одна повторная попытка (сеть могла моргнуть); если и она мимо — открываем
    // панель, обёртка попробует сама и покажет сообщение об ошибке
    preloadSearchPanel()
        .catch(() => preloadSearchPanel())
        .then(open, open);
}

export function closeSearch() {
    searchState.open = false;
}

/** Компонент панели: уже загружен — сразу, иначе через loadExtension (2 повтора при сбое сети) */
export const SearchPanelAsync = defineAsyncComponent({
    loader: () => {
        const loaded = loadedPanel();
        if (loaded) {
            return Promise.resolve(loaded);
        }
        return preloadSearchPanel().then(() => {
            const component = loadedPanel();
            if (!component) {
                throw new Error(`vpl.tas: в ${SEARCH_EXTENSION} нет компонента ${SEARCH_PANEL_COMPONENT}`);
            }
            return component;
        });
    },
    // ожидание показывает полоса индикатора (openSearch), а не заглушка на экране:
    // панель появляется сразу готовой. Сюда попадаем лишь в редком случае — когда
    // группа не доехала и после повторной попытки и обёртка пробует сама
    loadingComponent: { render: () => null },
    errorComponent: {
        template: `
            <div style="position:fixed;inset:0;z-index:1000;background:rgba(108,100,92,.35)" @click="$emit('close')">
                <div style="position:fixed;top:38px;left:50%;transform:translateX(-50%);width:min(980px,calc(100vw - 32px));padding:24px;border-radius:14px;background:#fff;text-align:center">
                    Не удалось загрузить поиск. <a href="#" @click.prevent="reload">Обновить страницу</a>
                </div>
            </div>
        `,
        emits: ['close'],
        methods: { reload() { window.location.reload(); } },
    },
    delay: 0,
    suspensible: false,
    onError(error, retry, fail, attempts) {
        if (attempts <= 2) {
            retry();
        } else {
            console.error(error);
            fail();
        }
    },
});
